import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Booking, VehicleBlock } from '../api';
import { parseUtc, formatDatetime } from '../utils/dates';
import type { Vehicle } from '../types';

const DAY_NAMES = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

// Mandag i ugen der indeholder `date` (lokal tid)
function getMonday(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay() || 7;
  if (day !== 1) d.setDate(d.getDate() - (day - 1));
  return d;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function localKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatWeekRange(weekStart: Date, weekEnd: Date): string {
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const sameYear  = weekStart.getFullYear() === weekEnd.getFullYear();
  if (sameMonth && sameYear) {
    return `${weekStart.getDate()}.–${weekEnd.getDate()}. ${weekStart.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}`;
  }
  if (sameYear) {
    return `${weekStart.getDate()}. ${weekStart.toLocaleDateString('da-DK', { month: 'short' })} – ${weekEnd.getDate()}. ${weekEnd.toLocaleDateString('da-DK', { month: 'short', year: 'numeric' })}`;
  }
  return `${weekStart.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })} – ${weekEnd.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

interface Props {
  vehicles: Vehicle[];
  bookings: Booking[];
  // Værksted, vedligehold, syn, eftersyn markeret "blokeret for udlejning" — egen farve
  blocks?: VehicleBlock[];
  onSelectBooking: (b: Booking) => void;
}

// Dansk kalenderdato for en UTC-tid fra API'et. Før blev UTC-datoen brugt direkte, så en booking
// der starter kl. 00:30 dansk tid havnede på dagen før.
const dkDate = (utc: string) =>
  parseUtc(utc).toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' });

type LaneItem =
  | { type: 'booking'; key: string; start: string; end: string; booking: Booking }
  | { type: 'block'; key: string; start: string; end: string; block: VehicleBlock };

export default function SwimLaneCalendar({ vehicles, bookings, blocks = [], onSelectBooking }: Props) {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));

  const days    = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = days[6];
  const todayKey = localKey(new Date());

  // Saml bookings og blokeringer pr. køretøj, kun dem der overlapper denne uge
  const itemsByVehicle = useMemo(() => {
    const fromStr = localKey(weekStart);
    const toStr   = localKey(weekEnd);
    const map = new Map<number, LaneItem[]>();
    const add = (vehicleId: number, item: LaneItem) => {
      if (item.start > toStr || item.end < fromStr) return;
      const list = map.get(vehicleId) || [];
      list.push(item);
      map.set(vehicleId, list);
    };
    for (const b of bookings) {
      if (!b.vehicle_id || b.cancelled) continue;
      add(b.vehicle_id, { type: 'booking', key: `b${b.id}`, start: dkDate(b.starts_at), end: dkDate(b.ends_at), booking: b });
    }
    for (const bl of blocks) {
      // Slutter blokeringen ved midnat, er sidste dag dagen før
      const endMs = parseUtc(bl.ends_at).getTime() - 1000;
      const end = new Date(endMs).toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' });
      add(bl.vehicle_id, { type: 'block', key: `x${bl.kind}${bl.source_id}`, start: dkDate(bl.starts_at), end, block: bl });
    }
    // Sorter pr. bil efter start-dato
    for (const list of map.values()) list.sort((a, b) => a.start.localeCompare(b.start));
    return map;
  }, [bookings, blocks, weekStart, weekEnd]);

  return (
    <div className="bg-cream-50 rounded-card shadow-card-sm overflow-hidden">
      {/* Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-100">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeekStart(getMonday(new Date()))}
            className="px-3 py-1.5 text-sm rounded-lg border border-cream-100 hover:bg-cream-100 transition-colors"
          >
            {t('calendar.today', { defaultValue: 'I dag' })}
          </button>
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="p-1.5 rounded-lg hover:bg-cream-100 transition-colors"
            aria-label="Forrige uge"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="p-1.5 rounded-lg hover:bg-cream-100 transition-colors"
            aria-label="Næste uge"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="text-base font-semibold text-ink-900 capitalize">
          {formatWeekRange(weekStart, weekEnd)}
        </div>
      </div>

      {/* Dag-headers */}
      <div className="grid grid-cols-[130px_repeat(7,1fr)] border-b border-cream-100 bg-[#ece9e2]">
        <div className="p-3 text-xs font-medium uppercase tracking-wide text-ink-500">Bil</div>
        {days.map(d => {
          const isToday = localKey(d) === todayKey;
          return (
            <div key={localKey(d)} className="p-2 text-center border-l border-cream-100">
              <div className="text-[11px] uppercase tracking-wide text-ink-500">
                {DAY_NAMES[d.getDay() === 0 ? 6 : d.getDay() - 1]}
              </div>
              <div className={`text-base font-semibold ${isToday ? 'text-sage-700' : 'text-ink-900'}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Køretøjs-rækker */}
      {vehicles.length === 0 ? (
        <div className="p-12 text-center text-ink-500">{t('vehicles.none_found')}</div>
      ) : (
        vehicles.map(v => {
          const vehicleItems = itemsByVehicle.get(v.id!) || [];
          return (
            <div
              key={v.id}
              className="grid grid-cols-[130px_repeat(7,1fr)] border-b border-cream-100 last:border-b-0"
            >
              {/* Bilnavn */}
              <div className="flex items-center px-3 py-3 border-r border-cream-100">
                <div className="min-w-0">
                  <div className="font-mono font-semibold text-sm text-ink-900">{v.registration_number}</div>
                  {(v.make || v.model) && (
                    <div className="text-xs text-ink-500 truncate">
                      {[v.make, v.model].filter(Boolean).join(' ')}
                    </div>
                  )}
                </div>
              </div>

              {/* Bookings i grid-flow — rækken vokser naturligt med antal bjælker */}
              <div className="col-span-7 relative min-h-[68px]">
                {/* Dag-baggrunde som absolut lag (klik passerer igennem) */}
                <div className="absolute inset-0 grid grid-cols-7 pointer-events-none">
                  {days.map(d => {
                    const isToday = localKey(d) === todayKey;
                    return (
                      <div
                        key={localKey(d)}
                        className={`border-l border-cream-100 ${isToday ? 'bg-sage-50/30' : ''}`}
                      />
                    );
                  })}
                </div>

                {/* Booking-bjælker — i normalt grid-flow med fast højde */}
                <div className="relative grid grid-cols-7 gap-y-1 p-1 content-start">
                  {vehicleItems.map(item => {
                    const bStart  = item.start;
                    const bEnd    = item.end;
                    const fromStr = localKey(weekStart);
                    const toStr   = localKey(weekEnd);

                    const effStart = bStart < fromStr ? fromStr : bStart;
                    const effEnd   = bEnd   > toStr   ? toStr   : bEnd;

                    const startIdx = days.findIndex(d => localKey(d) === effStart);
                    const endIdx   = days.findIndex(d => localKey(d) === effEnd);
                    if (startIdx < 0 || endIdx < 0) return null;

                    const startCol = startIdx + 1;
                    const span     = endIdx - startIdx + 1;
                    const truncStart = bStart < fromStr;
                    const truncEnd   = bEnd   > toStr;

                    if (item.type === 'block') {
                      const bl = item.block;
                      return (
                        <div
                          key={item.key}
                          style={{ gridColumn: `${startCol} / span ${span}` }}
                          className="h-7 px-2 rounded-md text-xs bg-amber-100 text-amber-800 border border-amber-300 truncate font-medium leading-[26px]"
                          title={`${bl.title} · ${formatDatetime(bl.starts_at)} → ${formatDatetime(bl.ends_at)}`}
                        >
                          {truncStart && '← '}
                          {bl.title}
                          {truncEnd && ' →'}
                        </div>
                      );
                    }
                    const b = item.booking;
                    return (
                      <button
                        key={item.key}
                        onClick={() => onSelectBooking(b)}
                        style={{ gridColumn: `${startCol} / span ${span}` }}
                        className="h-7 px-2 rounded-md text-xs bg-sage-100 hover:bg-sage-300 text-ink-900 truncate text-left transition-colors font-medium leading-7"
                        title={`${b.renter_name || '?'} · ${bStart} → ${bEnd}`}
                      >
                        {truncStart && '← '}
                        {b.renter_name || '—'}
                        {truncEnd && ' →'}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
